import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { MainMapService } from 'app/shared/components/main-map/main-map.service';
import { ScreenService } from 'app/shared/services/screen.service';
import { ArrayUtils } from 'app/shared/utils/array.utils';

export interface ResultActionEvent {
  index: number;
  item: __esri.Graphic;
}

export interface SelectionsChangesLike {
  adds?: __esri.Graphic[];
  deletes?: __esri.Graphic[];
}

@Component({
  selector: 'app-results-table-view',
  templateUrl: 'results-table-view.component.html',
  styleUrls: ['results-table-view.component.scss'],
})
export class ResultsTableViewComponent implements OnInit, OnChanges {
  @Input()
  layer: any;
  @Input()
  fields: __esri.Field[];
  @Input()
  resultList: __esri.Graphic[];

  @Input()
  elementsSelected: __esri.Graphic[];
  @Output()
  elementsSelectedChange = new EventEmitter<__esri.Graphic[]>(true);
  @Output()
  onSelectedChanged = new EventEmitter<SelectionsChangesLike>(true);

  @Input()
  pageIndex = 0;
  @Output()
  pageIndexChange = new EventEmitter<number>(true);

  allSelected = false;

  private graphicsSelectedSet = new Set<__esri.Graphic>();

  displayedColumns: string[];

  _pagedData: any[];
  pageSize = 9;

  isMobileModeObservable = this.screenService.observeIsMobile();

  constructor(private screenService: ScreenService) {}

  ngOnInit() {
    this.displayedColumns = [
      'selection_controls',
      ...this.fields.map((x) => x.name),
    ];
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.resultList) {
      this.onPageIndexChange(changes.pageIndex?.currentValue ?? 0);
    } else if (changes.pageIndex) {
      this.onPageIndexChange(this.pageIndex);
    }

    if (changes.elementsSelected) {
      this.graphicsSelectedSet = new Set(this.elementsSelected ?? []);
    }
  }

  onSelectAllChangedInternal(selected: boolean) {
    let changes: SelectionsChangesLike;

    const currentList = Array.from(this.graphicsSelectedSet.values());
    if (!selected) {
      changes = { deletes: currentList };
      this.graphicsSelectedSet.clear();
    } else {
      const adds = ArrayUtils.addRange(currentList, this.resultList);
      changes = { adds };
      this.graphicsSelectedSet = new Set(currentList);
    }

    this.emitSelectionsChanged(changes);
  }

  onColumnClick(dataIndex: number) {
    const elementIndex = this.dataIndexToListIndex(dataIndex);
    const graphic = this.resultList[elementIndex];

    let changes: SelectionsChangesLike;
    if (this.graphicsSelectedSet.has(graphic)) {
      this.graphicsSelectedSet.delete(graphic);
      changes = { deletes: [graphic] };
    } else {
      this.graphicsSelectedSet.add(graphic);
      changes = { adds: [graphic] };
    }

    this.emitSelectionsChanged(changes);
  }

  onPageIndexChange(pageIndex: number) {
    const lastPageIndex =
      Math.ceil((this.resultList?.length ?? 0) / this.pageSize) - 1 || 0;
    const newPageIndex = Math.min(pageIndex, lastPageIndex);

    const start = newPageIndex * this.pageSize;

    this._pagedData =
      this.resultList
        ?.slice(start, start + this.pageSize)
        ?.map((x) => x.attributes) ?? [];

    if (this.pageIndex !== newPageIndex) {
      this.pageIndex = newPageIndex;
      this.pageIndexChange.emit(this.pageIndex);
    }
  }

  isFieldUrl(field: __esri.Field) {
    return !!field?.name?.toLowerCase()?.includes('url');
  }

  isSelected(dataIndex: number) {
    const elementIndex = this.dataIndexToListIndex(dataIndex);
    const feature = this.resultList[elementIndex];
    return !!this.graphicsSelectedSet.has(feature);
  }

  private emitSelectionsChanged(changes?: SelectionsChangesLike) {
    this.elementsSelected = Array.from(this.graphicsSelectedSet.values());
    this.elementsSelectedChange.emit(this.elementsSelected);

    if (changes?.adds?.length || changes?.deletes?.length) {
      this.onSelectedChanged.emit(changes);
    }
  }

  private dataIndexToListIndex(dataIndex: number) {
    return dataIndex + this.pageIndex * this.pageSize;
  }
}
